import { useEffect, useMemo, useRef, useState } from "react";
import "@/App.css";
import ChatPanel from "@/components/ChatPanel";
import WelcomeModal from "@/components/WelcomeModal";
import CopyActions from "@/components/CopyActions";
import ProjectList from "@/components/ProjectList";
import SettingsModal from "@/components/SettingsModal";
import SummarySection from "@/components/SummarySection";
import TemplatePickerModal from "@/components/TemplatePickerModal";
import PrivacyPolicyModal from "@/components/PrivacyPolicyModal";

const STORAGE_KEY = "pcm_projects";
const SELECTED_KEY = "pcm_selected_id";
const SETTINGS_KEY = "pcm_settings";
const WELCOME_SEEN_KEY = "pcm_welcome_seen";
const BRIEF_TEMPLATE = "Stack: \nKey decisions: \nConstraints: \nOpen questions: ";

const BRIEF_TEMPLATES = [
  {
    id: "software",
    label: "Software",
    description: "Tech stack, decisions, and open questions",
    brief: "Stack: \nKey decisions: \nConstraints: \nOpen questions: ",
    copyPrompt:
      "Please summarize our conversation for my Context Savior project manager. Focus on: tech stack decisions, key decisions made, constraints established, and open questions still unresolved. Keep it concise and structured — no code blocks or small talk.",
    systemPrompt:
      "You are a project context extractor. Given a conversation, extract and summarize into exactly this format:\n\nStack: [tech stack OR primary components]\nKey decisions: [important decisions made]\nConstraints: [limitations, requirements, or rules]\nOpen questions: [unresolved questions]\n\nBe concise. One line per field. If nothing found for a field, write \"None\".",
  },
  {
    id: "writing",
    label: "Writing",
    description: "Genre, tone, themes, and characters",
    brief: "Genre/Format: \nTone & Voice: \nKey Themes: \nCharacters/POV: \nOpen Questions: ",
    copyPrompt:
      "Please summarize our conversation for my Context Savior project manager. Focus on: genre and format decided, tone and voice established, key themes identified, characters or POV discussed, and any open questions remaining. Keep it concise — no lengthy quotes or prose.",
    systemPrompt:
      "You are a project context extractor. Given a conversation, extract and summarize into exactly this format:\n\nGenre/Format: [type of writing or format]\nTone & Voice: [tone and style decisions]\nKey Themes: [main themes or ideas]\nCharacters/POV: [characters or point of view]\nOpen Questions: [unresolved questions]\n\nBe concise. One line per field. If nothing found for a field, write \"None\".",
  },
  {
    id: "research",
    label: "Research",
    description: "Topic, hypothesis, sources, and findings",
    brief: "Topic: \nHypothesis: \nKey Sources: \nFindings So Far: \nOpen Questions: ",
    copyPrompt:
      "Please summarize our conversation for my Context Savior project manager. Focus on: the research topic, any hypothesis or angle formed, key sources identified, findings or insights so far, and open questions still unresolved. Keep it concise and structured.",
    systemPrompt:
      "You are a project context extractor. Given a conversation, extract and summarize into exactly this format:\n\nTopic: [the research topic or question]\nHypothesis: [hypothesis or central argument]\nKey Sources: [sources, references, or data identified]\nFindings So Far: [insights or conclusions reached]\nOpen Questions: [unresolved questions]\n\nBe concise. One line per field. If nothing found for a field, write \"None\".",
  },
  {
    id: "general",
    label: "General",
    description: "Goal, context, and constraints",
    brief: "Goal: \nContext: \nConstraints: \nOpen Questions: ",
    copyPrompt:
      "Please summarize our conversation for my Context Savior project manager. Focus on: the main goal, context or background established, constraints or requirements identified, and open questions remaining. Keep it concise and structured.",
    systemPrompt:
      "You are a project context extractor. Given a conversation, extract and summarize into exactly this format:\n\nGoal: [the main objective]\nContext: [relevant background or context]\nConstraints: [limitations, requirements, or rules]\nOpen Questions: [unresolved questions]\n\nBe concise. One line per field. If nothing found for a field, write \"None\".",
  },
];

const PROVIDERS = [
  { id: "anthropic", label: "Claude (Anthropic)" },
  { id: "openai", label: "GPT-4o (OpenAI)" },
  { id: "gemini", label: "Gemini (Google)" },
  { id: "azure", label: "Copilot (Azure OpenAI)" },
  { id: "xai", label: "Grok (xAI)" },
  { id: "groq", label: "Llama via Groq" },
];

const DEFAULT_SETTINGS = {
  activeProvider: "anthropic",
  keys: {
    anthropic: "",
    openai: "",
    gemini: "",
    azure: "",
    xai: "",
    groq: "",
  },
  azureEndpoint: "",
  azureDeployment: "",
};

const TOKEN_WARN = 1500;
const TOKEN_DANGER = 2000;

const BriefCounter = ({ brief }) => {
  const chars = brief.length;
  const tokens = Math.ceil(chars / 4);
  const tokenClass =
    tokens >= TOKEN_DANGER
      ? "pcm-counter--danger"
      : tokens >= TOKEN_WARN
        ? "pcm-counter--warn"
        : "";
  return (
    <span className={`pcm-counter ${tokenClass}`} data-testid="brief-counter">
      {chars.toLocaleString()} chars · ~{tokens.toLocaleString()} tokens
    </span>
  );
};

const createProject = (
  name = "New Project",
  brief = BRIEF_TEMPLATE,
  allowEmpty = false,
) => {
  const id =
    typeof crypto !== "undefined" && crypto.randomUUID
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  return { id, name, brief, allowEmpty, updatedAt: new Date().toISOString() };
};

const getInitialState = () => {
  if (typeof window === "undefined") {
    const project = createProject();
    return { projects: [project], selectedId: project.id };
  }

  try {
    const storedProjects = JSON.parse(
      localStorage.getItem(STORAGE_KEY) || "[]",
    );
    const projects =
      Array.isArray(storedProjects) && storedProjects.length
        ? storedProjects.map((project) => {
            const allowEmpty = Boolean(project?.allowEmpty);
            const brief = typeof project?.brief === "string" ? project.brief : "";
            if (!allowEmpty && brief.trim() === "") {
              return { ...project, brief: BRIEF_TEMPLATE, allowEmpty: false };
            }
            return { ...project, brief, allowEmpty };
          })
        : [createProject()];
    let selectedId = localStorage.getItem(SELECTED_KEY);
    if (!selectedId || !projects.some((project) => project.id === selectedId)) {
      selectedId = projects[0].id;
    }
    return { projects, selectedId };
  } catch (error) {
    const project = createProject();
    return { projects: [project], selectedId: project.id };
  }
};

const getInitialSettings = () => {
  if (typeof window === "undefined") {
    return DEFAULT_SETTINGS;
  }
  try {
    const stored =
      JSON.parse(sessionStorage.getItem(SETTINGS_KEY) || "{}") || {};
    return {
      ...DEFAULT_SETTINGS,
      ...stored,
      keys: { ...DEFAULT_SETTINGS.keys, ...(stored.keys || {}) },
    };
  } catch (error) {
    return DEFAULT_SETTINGS;
  }
};

function App() {
  useEffect(() => {
    document.documentElement.classList.add("dark");
    return () => document.documentElement.classList.remove("dark");
  }, []);

  const initialState = useMemo(() => getInitialState(), [getInitialState]);
  const initialSettings = useMemo(
    () => getInitialSettings(),
    [getInitialSettings],
  );
  const [projects, setProjects] = useState(initialState.projects);
  const [selectedId, setSelectedId] = useState(initialState.selectedId);
  const [toast, setToast] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [draftName, setDraftName] = useState("");
  const [isSummaryOpen, setIsSummaryOpen] = useState(false);
  const [conversationText, setConversationText] = useState("");
  const [isSummarizing, setIsSummarizing] = useState(false);
  const [summaryError, setSummaryError] = useState("");
  const [settings, setSettings] = useState(initialSettings);
  const [draftSettings, setDraftSettings] = useState(initialSettings);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isTemplatePickerOpen, setIsTemplatePickerOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("brief");
  const [isWelcomeOpen, setIsWelcomeOpen] = useState(
    () => !localStorage.getItem(WELCOME_SEEN_KEY),
  );
  const [isPrivacyOpen, setIsPrivacyOpen] = useState(false);
  const fileInputRef = useRef(null);

  const selectedProject =
    projects.find((project) => project.id === selectedId) || projects[0];

  useEffect(() => {
    if (projects.length && !selectedProject) {
      setSelectedId(projects[0].id);
    }
  }, [projects, selectedProject, setSelectedId]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(projects));
    localStorage.setItem(SELECTED_KEY, selectedId);
  }, [projects, selectedId, STORAGE_KEY, SELECTED_KEY]);

  useEffect(() => {
    if (!toast) return undefined;
    const timer = setTimeout(() => setToast(null), 3000);
    return () => clearTimeout(timer);
  }, [toast, setToast]);

  useEffect(() => {
    if (isSettingsOpen) {
      setDraftSettings(settings);
    }
  }, [isSettingsOpen, settings, setDraftSettings]);

  useEffect(() => {
    if (typeof conversationText !== "string") {
      setConversationText("");
    }
  }, [conversationText, setConversationText]);

  useEffect(() => {
    setConversationText("");
    setSummaryError("");
  }, [selectedId]);

  const copyText = useMemo(() => {
    if (!selectedProject) {
      return "Project: \nContext: ";
    }
    return `Project: ${selectedProject.name}\nContext: ${selectedProject.brief}`;
  }, [selectedProject]);

  const handleAddProject = () => {
    setIsTemplatePickerOpen(true);
  };

  const handlePickTemplate = (templateId) => {
    const template = BRIEF_TEMPLATES.find((t) => t.id === templateId);
    const newProject = {
      ...createProject("New Project", template ? template.brief : BRIEF_TEMPLATE),
      templateId: template ? template.id : "software",
    };
    setProjects((prev) => [newProject, ...prev]);
    setSelectedId(newProject.id);
    setEditingId(newProject.id);
    setDraftName(newProject.name);
    setIsTemplatePickerOpen(false);
  };

  const handleBriefChange = (event) => {
    if (!selectedProject) return;
    const value = event.target.value;
    const shouldTemplate = !selectedProject.allowEmpty;
    const nextValue = shouldTemplate && value.trim() === "" ? BRIEF_TEMPLATE : value;
    setProjects((prev) =>
      prev.map((project) =>
        project.id === selectedProject.id
          ? { ...project, brief: nextValue, updatedAt: new Date().toISOString() }
          : project,
      ),
    );
  };

  const handleDeleteProject = (projectId) => {
    setProjects((prev) => {
      const filtered = prev.filter((project) => project.id !== projectId);
      if (filtered.length === 0) {
        const replacement = createProject();
        setSelectedId(replacement.id);
        return [replacement];
      }
      if (projectId === selectedId) {
        setSelectedId(filtered[0].id);
      }
      return filtered;
    });
    if (editingId === projectId) {
      setEditingId(null);
      setDraftName("");
    }
  };

  const handleCopy = async () => {
    if (!selectedProject) return;
    try {
      await navigator.clipboard.writeText(copyText);
      setToast({ message: "Copied to clipboard ✓", testId: "copy-success-message" });
    } catch (error) {
      const fallback = document.createElement("textarea");
      fallback.value = copyText;
      fallback.setAttribute("readonly", "");
      fallback.style.position = "absolute";
      fallback.style.left = "-9999px";
      document.body.appendChild(fallback);
      fallback.select();
      document.execCommand("copy");
      document.body.removeChild(fallback);
      setToast({ message: "Copied to clipboard ✓", testId: "copy-success-message" });
    }
  };

  const buildUniqueName = (baseName, existingNames) => {
    const trimmed = baseName?.trim() || "Untitled Project";
    if (!existingNames.has(trimmed)) {
      return trimmed;
    }
    let candidate = `${trimmed} (imported)`;
    let counter = 2;
    while (existingNames.has(candidate)) {
      candidate = `${trimmed} (imported ${counter})`;
      counter += 1;
    }
    return candidate;
  };

  const handleExport = () => {
    const payload = {
      projects: projects.map((project) => ({
        name: project.name,
        brief: project.brief,
      })),
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "context-bridge-backup.json";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleImportFile = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const text = await file.text();
      const data = JSON.parse(text);
      const incoming = Array.isArray(data?.projects) ? data.projects : [];
      if (!incoming.length) {
        setToast({ message: "0 projects imported ✓", testId: "import-success-message" });
        return;
      }

      const existingNames = new Set(projects.map((project) => project.name));
      const importedProjects = incoming.map((project) => {
        const name = buildUniqueName(project?.name, existingNames);
        existingNames.add(name);
        const brief = typeof project?.brief === "string" ? project.brief : "";
        const allowEmpty = brief.trim() === "";
        return createProject(name, brief, allowEmpty);
      });

      setProjects((prev) => [...importedProjects, ...prev]);
      if (importedProjects.length) {
        setSelectedId(importedProjects[0].id);
      }
      setToast({
        message: `${importedProjects.length} projects imported ✓`,
        testId: "import-success-message",
      });
    } catch (error) {
      setToast({ message: "Import failed", testId: "import-error-message" });
    } finally {
      event.target.value = "";
    }
  };

  const extractBriefValue = (briefText, label) => {
    if (!briefText) return "None";
    const lines = briefText.split("\n");
    const match = lines.find((line) =>
      line.trim().toLowerCase().startsWith(label),
    );
    if (!match) return "None";
    const value = match.split(":").slice(1).join(":").trim();
    return value || "None";
  };

  const handleCopyForManus = async () => {
    if (!selectedProject) return;
    const stack = extractBriefValue(selectedProject.brief, "stack");
    const decisions = extractBriefValue(selectedProject.brief, "key decisions");
    const constraints = extractBriefValue(selectedProject.brief, "constraints");
    const questions = extractBriefValue(selectedProject.brief, "open questions");
    const manusText =
      `You are assisting with: ${selectedProject.name}\n` +
      `Tech context: ${stack}\n` +
      `Already decided: ${decisions}\n` +
      `You must respect: ${constraints}\n` +
      `Still unresolved: ${questions}`;

    try {
      await navigator.clipboard.writeText(manusText);
      setToast({ message: "Copied for Manus ✓", testId: "copy-manus-message" });
    } catch (error) {
      const fallback = document.createElement("textarea");
      fallback.value = manusText;
      fallback.setAttribute("readonly", "");
      fallback.style.position = "absolute";
      fallback.style.left = "-9999px";
      document.body.appendChild(fallback);
      fallback.select();
      document.execCommand("copy");
      document.body.removeChild(fallback);
      setToast({ message: "Copied for Manus ✓", testId: "copy-manus-message" });
    }
  };

  const handleSaveSettings = () => {
    setSettings(draftSettings);
    sessionStorage.setItem(SETTINGS_KEY, JSON.stringify(draftSettings));
    setIsSettingsOpen(false);
  };

  const getSessionSettings = () => {
    try {
      const stored = JSON.parse(sessionStorage.getItem(SETTINGS_KEY) || "{}") || {};
      return {
        ...settings,
        ...stored,
        keys: { ...settings.keys, ...(stored.keys || {}) },
      };
    } catch (error) {
      return settings;
    }
  };

  const handleDraftSettingsChange = (nextSettings) => {
    setDraftSettings(nextSettings);
  };

  const handleConversationChange = (eventOrValue) => {
    const nextValue =
      typeof eventOrValue === "string"
        ? eventOrValue
        : eventOrValue?.target?.value ?? "";
    setConversationText(nextValue);
    if (summaryError) {
      setSummaryError("");
    }
  };

  const handleGenerateSummary = async () => {
    if (!selectedProject || isSummarizing) return;
    if (!conversationText.trim()) {
      setSummaryError("Paste a conversation to summarize.");
      setIsSummaryOpen(true);
      return;
    }

    const activeSettings = getSessionSettings();
    const provider = activeSettings.activeProvider;
    const providerKey = activeSettings.keys?.[provider]?.trim();
    if (!providerKey) {
      setSummaryError("Please add an API key for this provider in Settings.");
      setIsSummaryOpen(true);
      return;
    }
    if (provider === "azure") {
      const endpoint = activeSettings.azureEndpoint?.trim();
      const deployment = activeSettings.azureDeployment?.trim();
      if (!endpoint || !deployment) {
        setSummaryError(
          "Please add your Azure endpoint URL and deployment name in Settings.",
        );
        setIsSummaryOpen(true);
        return;
      }
    }

    if (selectedProject.brief && selectedProject.brief.trim()) {
      const confirmed = window.confirm("Replace existing brief?");
      if (!confirmed) return;
    }

    setIsSummarizing(true);
    setSummaryError("");

    try {
      const activeTemplate = BRIEF_TEMPLATES.find(
        (t) => t.id === (selectedProject.templateId || "software"),
      );
      const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
      const response = await fetch(`${backendUrl}/api/summary`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          conversation: conversationText,
          api_key: providerKey,
          provider: provider,
          system_prompt: activeTemplate?.systemPrompt,
        }),
      });
      const payload = await response.json().catch(() => ({}));

      if (!response.ok) {
        const errorMessage =
          payload?.detail ||
          payload?.error?.message ||
          "Failed to summarize.";
        throw new Error(errorMessage);
      }

      let summary = "";
      summary = payload?.summary || "";
      const trimmedSummary = summary.trim();
      if (!trimmedSummary) {
        throw new Error("No summary returned.");
      }

      setProjects((prev) =>
        prev.map((project) =>
          project.id === selectedProject.id
            ? { ...project, brief: trimmedSummary }
            : project,
        ),
      );
      setToast({ message: "Brief updated ✓", testId: "summary-success-message" });
      setSummaryError("");
    } catch (error) {
      setSummaryError(error.message || "Failed to summarize.");
    } finally {
      setIsSummarizing(false);
    }
  };

  const handleWelcomeDismiss = (dontShowAgain) => {
    if (dontShowAgain) {
      localStorage.setItem(WELCOME_SEEN_KEY, "1");
    }
    setIsWelcomeOpen(false);
  };

  const handleWelcomeComplete = (templateId, openImport) => {
    const template = BRIEF_TEMPLATES.find((t) => t.id === templateId);
    setProjects((prev) =>
      prev.map((p) =>
        p.id === selectedId
          ? {
              ...p,
              brief: template ? template.brief : BRIEF_TEMPLATE,
              templateId: template ? template.id : "software",
              updatedAt: new Date().toISOString(),
            }
          : p,
      ),
    );
    localStorage.setItem(WELCOME_SEEN_KEY, "1");
    setIsWelcomeOpen(false);
    if (openImport) {
      setActiveTab("brief");
      setIsSummaryOpen(true);
    }
  };

  const startEditing = (project) => {
    setEditingId(project.id);
    setDraftName(project.name);
  };

  const cancelEditing = () => {
    setEditingId(null);
    setDraftName("");
  };

  const commitEditing = (project) => {
    if (!project) return;
    const trimmed = draftName.trim();
    const nextName = trimmed || project.name || "Untitled Project";
    setProjects((prev) =>
      prev.map((p) =>
        p.id === project.id
          ? { ...p, name: nextName, updatedAt: new Date().toISOString() }
          : p,
      ),
    );
    setEditingId(null);
    setDraftName("");
  };

  return (
    <div className="pcm-app" data-testid="project-context-manager-app">
      <div className="pcm-glow-top-right" aria-hidden />
      <div className="pcm-glow-bottom-left" aria-hidden />
      <div className="pcm-shell" data-testid="project-shell">
        <header className="pcm-topbar" data-testid="app-topbar">
          <h1 className="pcm-brand" data-testid="app-title">
            <img src="/contextsaviorlogo.svg" alt="" className="pcm-brand-logo" aria-hidden="true" />
            <span className="pcm-brand-text">Context Savior</span>
          </h1>
          <div className="pcm-topbar-actions" data-testid="topbar-actions">
            <button
              type="button"
              className="pcm-topbar-icon"
              onClick={() => setIsSettingsOpen(true)}
              data-testid="settings-button"
            >
              ⚙️
            </button>
          </div>
        </header>

        <div className="pcm-content" data-testid="main-content">
          <ProjectList
            projects={projects}
            selectedId={selectedId}
            editingId={editingId}
            draftName={draftName}
            onAddProject={handleAddProject}
            onSelectProject={setSelectedId}
            onDeleteProject={handleDeleteProject}
            onStartEdit={startEditing}
            onDraftNameChange={(event) => setDraftName(event.target.value)}
            onCommitEdit={commitEditing}
            onCancelEdit={cancelEditing}
          />

          <div className="pcm-tabs" data-testid="main-tabs">
            <button
              type="button"
              className={`pcm-tab ${activeTab === "brief" ? "active" : ""}`}
              onClick={() => setActiveTab("brief")}
              data-testid="tab-brief"
            >
              Context Brief
            </button>
            <button
              type="button"
              className={`pcm-tab ${activeTab === "chat" ? "active" : ""}`}
              onClick={() => setActiveTab("chat")}
              data-testid="tab-chat"
            >
              Chat
            </button>
          </div>

          {activeTab === "brief" ? (
            <div className="pcm-tab-content" data-testid="brief-tab">
              <section
                className="pcm-field pcm-textarea-field"
                data-testid="context-brief-field"
              >
                <label
                  htmlFor="context-brief"
                  className="pcm-label"
                  data-testid="context-brief-label"
                >
                  Context Brief
                </label>
                <textarea
                  id="context-brief"
                  className="pcm-textarea"
                  value={selectedProject?.brief || ""}
                  onChange={handleBriefChange}
                  placeholder="Stack, key decisions, open questions, constraints..."
                  data-testid="project-context-textarea"
                />
                <div className="pcm-brief-meta" data-testid="brief-meta">
                  <span className="pcm-save-status" data-testid="auto-save-indicator">
                    ✓ Saved
                  </span>
                  <BriefCounter brief={selectedProject?.brief || ""} />
                </div>
              </section>

              <SummarySection
                isOpen={isSummaryOpen}
                onToggle={() => setIsSummaryOpen((prev) => !prev)}
                conversationText={conversationText}
                onConversationChange={handleConversationChange}
                summaryError={summaryError}
                isSummarizing={isSummarizing}
                onGenerateSummary={handleGenerateSummary}
                copyPrompt={
                  BRIEF_TEMPLATES.find(
                    (t) => t.id === (selectedProject?.templateId || "software"),
                  )?.copyPrompt
                }
              />

              <CopyActions onCopy={handleCopy} onCopyForManus={handleCopyForManus} />
            </div>
          ) : (
            <ChatPanel
              projectId={selectedProject?.id}
              projectName={selectedProject?.name}
              brief={selectedProject?.brief || ""}
              getSessionSettings={getSessionSettings}
              backendUrl={process.env.REACT_APP_BACKEND_URL || ""}
            />
          )}
        </div>
      </div>

      <WelcomeModal
        isOpen={isWelcomeOpen}
        templates={BRIEF_TEMPLATES}
        onComplete={handleWelcomeComplete}
        onDismiss={handleWelcomeDismiss}
      />

      <TemplatePickerModal
        isOpen={isTemplatePickerOpen}
        templates={BRIEF_TEMPLATES}
        onPick={handlePickTemplate}
        onClose={() => setIsTemplatePickerOpen(false)}
      />

      <SettingsModal
        isOpen={isSettingsOpen}
        providers={PROVIDERS}
        draftSettings={draftSettings}
        onDraftSettingsChange={handleDraftSettingsChange}
        onClose={() => setIsSettingsOpen(false)}
        onSave={handleSaveSettings}
        onExport={handleExport}
        onImport={handleImportFile}
        fileInputRef={fileInputRef}
      />

      <footer className="pcm-footer" data-testid="app-footer">
        <a href="mailto:hello@contextsavior.com" className="pcm-footer-link">
          hello@contextsavior.com
        </a>
        <span className="pcm-footer-sep">·</span>
        <button type="button" className="pcm-footer-link pcm-footer-btn" onClick={() => setIsPrivacyOpen(true)}>
          Privacy Policy
        </button>
      </footer>

      <PrivacyPolicyModal isOpen={isPrivacyOpen} onClose={() => setIsPrivacyOpen(false)} />

      {toast ? (
        <div className="pcm-toast" data-testid={toast.testId}>
          {toast.message}
        </div>
      ) : null}
    </div>
  );
}

export default App;
